import { action, observable, makeObservable } from 'mobx';
import { UserEditForm } from 'modules/user/forms';
import { BaseViewStore } from 'core/stores';
import { formInvalidate, notificationError, generateRandomString, isSuperAdminOrganization } from 'core/utils';
import { roles as allRoles, warehouseRoles } from 'common/constants';
import { PrintNodeInfoStore } from 'modules/print-node-info/stores';
import { CoreUserInfoStore } from 'modules/core-user-info/stores';
import { lookupRouteName } from 'common/constants';
import { LookupStore } from 'modules/lookup/stores';
import { FulfillmentUserStore } from 'modules/membership/stores';
import _ from 'lodash';
import { ApplicationStore } from 'platform/application/stores';
import { CoreUserService } from 'common/data';
import { BaasicDropdownViewStore } from 'common/stores';
import UserStore from './UserStore';

class UserEditViewStore extends BaseViewStore {
	@observable showNotification = false;
	@observable enableEmailNotification = null;
	@observable canSetEnableEmailNotification = false;
	@observable hasPiiAccess = null;
	@observable canSetPii = false;
	loggedUser = null;
	notification = null;
	user;
	userInOrganization;
	@observable userPhone = null;
	@observable isHavingAPISecret = false;
	initialPiiAccess = false;
	timeouts = [];
	isEditMode = true;
	isSuperAdmin = false;
	form = new UserEditForm({
		onSuccess: async () => {
			const userPhone = this.form.$('userPhone').value;
			if (!this.checkUserPhone() && userPhone) {
				this.form.$('userPhone').invalidate('LOGINFORM.INVALIDUSERPHONEFORMAT');
			} else {
				this.suspend();
				await this.updateUser();
				this.resume();
				this.modalParams.success();
			}
		},
		onError: async () => {
			const userPhone = this.form.$('userPhone').value;
			if (!this.checkUserPhone() && userPhone) {
				this.form.$('userPhone').invalidate('LOGINFORM.INVALIDUSERPHONEFORMAT');
			}
		},
	});

	constructor(rootStore, { modalParams }) {
		super(rootStore);
		makeObservable(this);
		this.modalParams = modalParams;
		this.loggedUser = rootStore.authStore.user;
		this.listStore = rootStore.userListViewStore.listStore;
		this.applicationStore = new ApplicationStore(rootStore);
		this.printNodeInfoStore = new PrintNodeInfoStore(rootStore);
		this.coreUserInfoStore = new CoreUserInfoStore(rootStore);
		this.lookupStore = new LookupStore(rootStore);
		this.fulfillmentUserStore = new FulfillmentUserStore(rootStore);
		this.notification = 'LOGINFORM.PRINTNODE.NOTIFICATIONS.REQUIRED';
		this.isSuperAdmin = isSuperAdminOrganization(rootStore);
		this.userService = new CoreUserService(rootStore.app.baasic.apiClient);
		this.initialize();
	}

	@action
	initialize = async () => {
		this.suspend();
		this.form.reset();
		await this.getUser(this.rootStore.routerStore.routerState.queryParams.editId);
		await this.initializeDropdowns();
		this.user = this.listStore.item;

		await Promise.all([this.getPrintNodeInfo(this.user.id), this.getCoreUserInfoAndCheckPermission(this.user.id)]);

		const { roles, ...other } = this.listStore.item;
		this.form.update(other);
		this.roleDropdownStore.setValue(roles);
		this.isMainAccountOwner =
			_.findIndex(
				roles,
				role => role.name === allRoles.warehouseAccountOwner || role.name === allRoles.sellerAccountOwner
			) != -1 && this.user.isMainOwner;
		if ((this.user.id != this.rootStore.authStore.user.id && this.hasPiiAccess) || this.isSuperAdmin) {
			this.canSetPii = true;
		} else {
			this.canSetPii = false;
		}

		const loggedUserRole = _.find(this.loggedUser.roles, role => role == warehouseRoles.warehouseAccountOwner);
		if (loggedUserRole) {
			this.canSetEnableEmailNotification = true;
		} else {
			this.canSetEnableEmailNotification = false;
		}
		this.resume();
	};

	@action.bound
	initializeDropdowns = async () => {
		this.userInOrganization = await this.userService.find({
			userId: this.listStore.item.id,
			embed: ['organization', 'organization.organizationType'],
		});

		this.countryDropdownStore = new BaasicDropdownViewStore();
		this.roleDropdownStore = new BaasicDropdownViewStore();
		this.labelPrinterDropdownStore = new BaasicDropdownViewStore();
		this.packingSlipPrinterDropdownStore = new BaasicDropdownViewStore();
		await Promise.all([
			this.countryDropdownStore.initialize({
				field: this.form.$('countryCodeId'),
				store: new LookupStore(this.rootStore),
				lookupRoute: lookupRouteName.countryCustom,
				filterCallback: options => {
					return options.map(option => {
						return {
							value: option.id,
							label: `${option.name} | ${option.phoneCode}`,
							extra: option,
						};
					});
				},
			}),
			this.roleDropdownStore.initialize({
				field: this.form.$('roleIds'),
				store: new UserStore(this.rootStore),
				fetchFunc: 'fetchRoles',
				filterProperties: {
					organizationTypeName: _.get(this.userInOrganization.data.item[0], 'organization.organizationType.name'),
				},
				isMulti: true,
			}),
			this.labelPrinterDropdownStore.initialize({
				field: this.form.$('labelPrinterId'),
				store: new PrintNodeInfoStore(this.rootStore),
				fetchFunc: 'getPrinters',
				fetchInitialValues: false,
			}),
			this.packingSlipPrinterDropdownStore.initialize({
				field: this.form.$('packingSlipPrinterId'),
				store: new PrintNodeInfoStore(this.rootStore),
				fetchFunc: 'getPrinters',
				fetchInitialValues: false,
			}),
		]);
	};

	@action
	getUser = async id => {
		await this.listStore.getUser(id);
	};

	@action
	getCoreUserInfoAndCheckPermission = async userId => {
		await this.coreUserInfoStore.findCoreUserInfo({ userId: userId, embed: ['coreCountry'] });
		if (this.coreUserInfoStore.response.totalRecords != 0) {
			const coreUserInfo = this.coreUserInfoStore.response.item[0];
			this.hasPiiAccess = coreUserInfo.piiAccess;
			this.initialPiiAccess = coreUserInfo.piiAccess;
			if (coreUserInfo.coreCountry) {
				this.countryDropdownStore.setValue({
					value: coreUserInfo.coreCountry.id,
					label: `${coreUserInfo.coreCountry.phoneCode} | ${coreUserInfo.coreCountry.name}`,
					extra: coreUserInfo.coreCountry,
				});
			}

			let arr = coreUserInfo.userPhone ? coreUserInfo.userPhone.split(' ') : [];
			if (arr.length > 1) {
				arr.shift();
			}
			this.form.$('userPhone').set('value', arr.join(''));
			if (coreUserInfo.apiSecret != '') {
				this.isHavingAPISecret = true;
				this.form.$('apiSecret').set('value', coreUserInfo.apiSecret);
			}
			this.enableEmailNotification = coreUserInfo.enableEmailNotification;
		} else {
			this.hasPiiAccess = false;
		}
	};

	@action
	getPrintNodeInfo = async userId => {
		await this.printNodeInfoStore.findPrintNodeInfo({ userId: userId });
		if (this.printNodeInfoStore.response.totalRecords > 0) {
			const printNodeInfo = this.printNodeInfoStore.response.item[0];
			if (printNodeInfo.apiKey != '') {
				this.form.$('apiKey').value = printNodeInfo.apiKey;
			}
			if (printNodeInfo.labelPrinterId != '' && printNodeInfo.labelPrinterName != '') {
				this.labelPrinterDropdownStore.setValue({
					label: printNodeInfo.labelPrinterName,
					value: printNodeInfo.labelPrinterId,
				});
			}
			if (printNodeInfo.packingSlipPrinterId != '' && printNodeInfo.packingSlipPrinterName != '') {
				this.packingSlipPrinterDropdownStore.setValue({
					label: printNodeInfo.packingSlipPrinterName,
					value: printNodeInfo.packingSlipPrinterId,
				});
			}
			if (printNodeInfo.apiKey != '') {
				await Promise.all([
					this.labelPrinterDropdownStore.updateFilter({ apiKey: printNodeInfo.apiKey }),
					this.packingSlipPrinterDropdownStore.updateFilter({ apiKey: printNodeInfo.apiKey }),
				]);
				if (this.labelPrinterDropdownStore.error) this.handlePrinterError();
			}
		}
	};

	@action
	updateUser = async () => {
		if (!(await this.checkEmail())) {
			this.form.invalidate('USEREDIT.INVALIDEMAIL');
			return;
		}
		const { roleIds, ...other } = this.form.values();
		Object.assign(this.user, other);
		this.user.roles = _.map(this.roleDropdownStore.value, role => {
			return { name: role.label, id: role.value };
		});
		if (!this.canSetPii) {
			this.hasPiiAccess = this.initialPiiAccess;
		}
		try {
			let _userId = this.rootStore.routerStore.routerState.queryParams.editId;
			let userObj = {
				id: _userId,
				piiAccess: this.hasPiiAccess,
				apiSecret: this.user.apiSecret,
				enableEmailNotification: this.enableEmailNotification,
				countryId: this.form.$('countryCodeId').value,
				...this.user,
				labelPrinterName: _.get(this.labelPrinterDropdownStore, 'value.label'),
				packingSlipPrinterName: _.get(this.packingSlipPrinterDropdownStore, 'value.label'),
				userPhone: _.get(this.countryDropdownStore, 'value.extra.phoneCode') + ' ' + this.form.$('userPhone').value,
			};

			await this.listStore.updateUser(userObj);
			this.rootStore.notificationStore.success(['LOOKUPUPDATE.USER', { user: this.user.userName.split('-')[0] }]);
		} catch (err) {
			formInvalidate({ form: this.form, err });
		}
	};

	@action.bound
	checkEmail = async () => {
		const { email } = this.form.values();
		if (email.trim() !== this.user.email.trim()) {
			const response = await this.fulfillmentUserStore.validateEmail(email);
			return response.data;
		}
		return true;
	};

	@action.bound
	getPrinters = async () => {
		if (this.form.$('apiKey').value.trim() == '') {
			this.showNotification = true;
			return;
		}
		this.showNotification = false;
		await this.clearPrinters();
		if (this.labelPrinterDropdownStore.error) {
			this.handlePrinterError();
		} else if (this.labelPrinterDropdownStore.options.length > 0)
			this.rootStore.notificationStore.success('LOGINFORM.PRINTNODE.NOTIFICATIONS.PRINTERSFOUND');
		else this.rootStore.notificationStore.success('LOGINFORM.PRINTNODE.NOTIFICATIONS.PRINTERSNOTFOUND');
	};

	@action.bound
	handlePrinterError = () => {
		if (_.get(this.labelPrinterDropdownStore, 'error.data.isSuccess') === false) {
			this.rootStore.notificationStore.error('LOGINFORM.PRINTNODE.NOTIFICATIONS.INVALIDAPIKEY');
			this.labelPrinterDropdownStore.error = null;
		} else {
			notificationError({ store: this.rootStore.notificationStore, err: this.labelPrinterDropdownStore.error });
		}
	};

	@action.bound
	clearPrinters = async () => {
		for (const dropdown of [this.labelPrinterDropdownStore, this.packingSlipPrinterDropdownStore]) {
			dropdown.setValue(null);
			await dropdown.updateFilter({ apiKey: this.form.$('apiKey').value });
		}
	};

	@action.bound
	onCheckPiiAccess = props => {
		if (props) {
			this.hasPiiAccess = props.currentTarget.checked;
		}
	};

	@action.bound
	onCheckEnableEmailNotification = props => {
		if (props) {
			this.enableEmailNotification = props.currentTarget.checked;
		}
	};

	onUserPhoneChange = value => {
		// Automatically correct user phone input 2.5 seconds after user stops typing
		const highestTimeoutId = setTimeout(() => {
			this.form.$('userPhone').resetValidation();
			this.timeouts.forEach(timeoutId => {
				if (timeoutId < highestTimeoutId) {
					clearTimeout(timeoutId);
				}
			});
			this.timeouts.push(
				setTimeout(() => {
					this.correctUserPhone(value);
				}, 2500)
			);
		}, 0);
	};

	@action.bound
	correctUserPhone = () => {
		const regex = /^\d+$/;
		var value = this.form.$('userPhone').value;
		if (!value.match(regex) && value) {
			value = value.replace(/\D/g, '');
			this.form.$('userPhone').value = value;
		}
	};

	@action.bound
	checkUserPhone = () => {
		let value = this.form.$('userPhone').value;
		if (value) {
			const regex = /^\d+$/;
			var phone = _.get(this.countryDropdownStore, 'value.extra.phoneCode') + value;
			if ((!value.match(regex) && value.length > 0) || (phone.length < 7 || phone.length > 15)) {
				return false;
			} else {
				return true;
			}
		}
	};

	@action.bound generateApiSecret() {
		this.isHavingAPISecret = true;
		var radnomStringGenerated = generateRandomString(32);
		this.form.$('apiSecret').set('value', radnomStringGenerated);
	}
}

export default UserEditViewStore;



// WEBPACK FOOTER //
// ./src/modules/user/stores/UserEditViewStore.js